import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    IconButton,
    Typography,
    Grid,
    Switch,
    FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../api/api';
import { LoadingButton } from '@mui/lab';
import en_ja from '../../translations/en_ja';

const AddPharmacy = ({
    dialogOpen,
    onClose,
    editMode = false,
    onSubmit,
    setSnackbarType,
    setSnackbarOpen,
    setSnackbarMessage,
    pharmacyData
}) => {
    const userDetails = JSON.parse(localStorage.getItem("user"));

    const [formData, setFormData] = useState({
        name: '',
        address: '',
        city: '',
        postal_code: '',
        phone_number: '',
        email: '',
        status: true,
        created_by: userDetails?.id
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const [originalData, setOriginalData] = useState(null);
    const [isChange, setIsChange] = useState(false);

    const validateForm = () => {
        const newErrors = {};
        if (!(formData.name || "").trim()) newErrors.name = en_ja['Pharmacy name is required'];
        if (!(formData.address || "").trim()) newErrors.address = en_ja['Address is required'];
        if (!(formData.city || "").trim()) newErrors.city = en_ja['City is required'];
        if (!(formData.postal_code || "").trim()) newErrors.postal_code = en_ja['Postal code is required'];

        if (!(formData.phone_number || "").trim()) newErrors.phone_number = en_ja['Phone number is required'];
        else if (!/^[0-9-]+$/.test(formData.phone_number)) newErrors.phone_number = en_ja['Enter only numbers'];

        if (!(formData.email || "").trim()) newErrors.email = en_ja['Email is required'];
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = en_ja['Enter a valid email address'];

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        if (editMode && pharmacyData) {
            const initialData = {
                id: pharmacyData.id,
                name: pharmacyData.pharmacy_name || "",
                address: pharmacyData.address || "",
                city: pharmacyData.city || "",
                postal_code: pharmacyData.postal_code || "",
                phone_number: pharmacyData.phone_number || "",
                email: pharmacyData.email || "",
                status: pharmacyData.status ?? true,
                created_by: pharmacyData.created_by || userDetails?.id,
            };

            setFormData(initialData);
            setOriginalData(initialData);
            setIsChange(false);
        }
    }, [editMode, pharmacyData]);

    useEffect(() => {
        if (editMode && originalData) {
            const hasChanges =
                JSON.stringify(formData) !== JSON.stringify(originalData);
            setIsChange(hasChanges);
        }
    }, [formData, originalData, editMode]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleStatusToggle = () => {
        setFormData(prev => ({ ...prev, status: !prev.status }));
    };

    const handleClose = () => {
        setFormData({
            name: '',
            address: '',
            city: '',
            postal_code: '',
            phone_number: '',
            email: '',
            status: true,
            created_by: userDetails?.id
        });
        setErrors({});
        onClose();
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setLoading(true);

        const obj = {
            name: formData.name,
            address: formData.address,
            phone_number: formData.phone_number,
            postal_code: formData.postal_code,
            city: formData.city,
            email: formData.email,
            created_by: formData.created_by,
            status: formData.status
        };

        try {
            let response;

            if (editMode) {
                response = await api.put(`/v1/admin/pharmacy/edit/${pharmacyData.id}`, obj);
            } else {
                response = await api.post("/v1/admin/add-pharmacy", obj);
            }

            if (response.status === 200 || response.status === 201) {
                setSnackbarMessage(response?.data?.message || (editMode ? en_ja['Pharmacy updated successfully'] : en_ja['Pharmacy added successfully']));
                setSnackbarOpen(true);
                setSnackbarType("success");
                if (onSubmit) onSubmit();
                handleClose();
            }
        } catch (error) {
            setSnackbarMessage(error?.response?.data?.error || error?.response?.data?.message || en_ja['Something went wrong.']);
            setSnackbarOpen(true);
            setSnackbarType("error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={dialogOpen} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        {editMode ? en_ja['Edit Pharmacy'] : en_ja['Add New Pharmacy']}
                    </Typography>
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <TextField
                    label={en_ja['Pharmacy Name']}
                    name="name"
                    fullWidth
                    value={formData.name}
                    onChange={handleChange}
                    margin="normal"
                    error={!!errors.name}
                    helperText={errors.name}
                />

                <TextField
                    label={en_ja['Address']}
                    name="address"
                    fullWidth
                    value={formData.address}
                    onChange={handleChange}
                    margin="normal"
                    error={!!errors.address}
                    helperText={errors.address}
                />

                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 3 }}>
                    <TextField
                        label={en_ja['City']}
                        name="city"
                        fullWidth
                        value={formData.city}
                        onChange={handleChange}
                        margin="normal"
                        error={!!errors.city}
                        helperText={errors.city}
                    />

                    <TextField
                        label={en_ja['Postal Code']}
                        name="postal_code"
                        fullWidth
                        value={formData.postal_code}
                        onChange={handleChange}
                        margin="normal"
                        error={!!errors.postal_code}
                        helperText={errors.postal_code}
                    />
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 3 }}>
                    <TextField
                        label={en_ja['Phone Number']}
                        name="phone_number"
                        fullWidth
                        value={formData.phone_number}
                        onChange={handleChange}
                        margin="normal"
                        error={!!errors.phone_number}
                        helperText={errors.phone_number}
                    />

                    <TextField
                        label={en_ja['Email']}
                        name="email"
                        fullWidth
                        value={formData.email}
                        onChange={handleChange}
                        margin="normal"
                        error={!!errors.email}
                        helperText={errors.email}
                    />
                </Box>

                <Grid item xs={12}>
                    <FormControlLabel
                        label={<Typography variant="subtitle2" fontWeight="bold">{en_ja['Active Status']}</Typography>}
                        control={<Switch checked={formData.status} onChange={handleStatusToggle} color="success" />}
                    />
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button onClick={handleClose} variant="outlined">
                    {en_ja['Cancel']}
                </Button>
                <LoadingButton
                    onClick={handleSubmit}
                    loading={loading}
                    variant="contained"
                    disabled={editMode && !isChange}
                    sx={{ backgroundColor: "rgba(220, 53, 69, 1)" }}
                >
                    {editMode ? en_ja['Update Pharmacy'] : en_ja['Add Pharmacy']}
                </LoadingButton>

            </DialogActions>
        </Dialog>
    );
};

export default AddPharmacy;
